import queryString from 'query-string';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import type { Maybe } from '@graphql/generated';
import { isValidCoordinates } from '@shared/core/places';
import type { LatLngLiteral } from '@shared/core/google-maps/types';

const VRBO_SEARCH_URL = 'https://www.vrbo.com/search';
const DEFAULT_GUEST_COUNT = '2';
const DEFAULT_SORT_ORDER = 'RECOMMENDED';

type GetVrboSearchUrlParams = Readonly<{
  destination?: Maybe<string>;
  location?: Maybe<LatLngLiteral>;
  checkInDate?: string;
  checkOutDate?: string;
  adults?: string;
}>;

/**
 * Builds a VRBO search URL with proper URL encoding.
 * Uses URLSearchParams internally via query-string for safe parameter encoding.
 *
 * @param params - Search parameters for VRBO
 * @returns Encoded VRBO search URL
 */
const getVrboSearchUrl = (params: GetVrboSearchUrlParams): string => {
  const { destination, location, checkInDate, checkOutDate, adults = DEFAULT_GUEST_COUNT } = params;

  const latLong = location && isValidCoordinates({ latitude: location.lat, longitude: location.lng }) ? `${location.lat},${location.lng}` : null;

  return queryString.stringifyUrl(
    {
      url: VRBO_SEARCH_URL,
      query: {
        destination: destination ?? null,
        latLong,
        startDate: checkInDate ?? null,
        endDate: checkOutDate ?? null,
        adults,
        sort: DEFAULT_SORT_ORDER,
      },
    },
    { skipNull: true, skipEmptyString: true },
  );
};

/**
 * Builds a VRBO search URL wrapped with the redirect service for tracking.
 * Ensures proper URL encoding to prevent injection attacks.
 *
 * @param params - Search parameters for VRBO
 * @returns Redirect service URL pointing to VRBO search
 */
export const getVrboRedirectUrl = (params: GetVrboSearchUrlParams): string => {
  const vrboUrl = getVrboSearchUrl(params);
  return formatRedirectURL({ url: vrboUrl }) ?? vrboUrl;
};
